/** Селекторы: чистые функции от состояния. */
import type { PlayerState, RetryLock } from "@newfungame/contracts";
import { balanceV1 } from "@newfungame/engine";
import type { AppState, BattleSpeed } from "./app-state";
import type { WindowRoute } from "./routes";

/** Окно, которое сейчас на экране. */
export function currentWindow(s: AppState): WindowRoute | null {
  return s.route.windows[0] ?? null;
}

/** Бой стоит: свёрнуто или открыто модальное окно. Вкладки бой не останавливают. */
export function isBattlePaused(s: AppState): boolean {
  return s.pause.length > 0 || currentWindow(s) !== null;
}

export const vipActive = (s: AppState): boolean => s.player?.vip.active ?? false;

/** Инструменты ускорения: флаг сервера. Журнал и запросы он всё равно проверяет сам. */
export const adminTools = (s: AppState): boolean => s.player?.player.adminTools ?? false;

/** Время сервера сейчас. */
export const serverNow = (s: AppState): number => Date.now() + s.clockOffset;

/** Действующий таймер после падения штаба. */
export function retryLockOf(s: AppState): RetryLock | null {
  const lock = s.player?.retryLock ?? null;
  return lock && !vipActive(s) && lock.until > serverNow(s) ? lock : null;
}

/** x2 открыта: VIP или час из обмена. */
export const speedOpen = (s: AppState): boolean => vipActive(s) || (s.player?.speedUntil ?? 0) > serverNow(s);

/** Скорость боя сейчас. */
export const battleSpeed = (s: AppState): BattleSpeed => {
  if (adminTools(s)) return s.speedPref;
  return speedOpen(s) ? (Math.min(s.speedPref, 2) as BattleSpeed) : 1;
};

/** Флаги прогресса — что показывать в HUD и вкладках. */
export interface ProgressFlags {
  /** Кнопка «Автонавыки» (с волны `balance.waves.autoSkillsFromWave`). */
  autoSkills: boolean;
  /** Открытые герои по порядку. */
  unlockedHeroes: PlayerState["heroes"][number]["heroId"][];
  /** Есть офлайн-доход к получению. */
  offlineIncome: boolean;
  /** Награду серии можно забрать сегодня. */
  streakClaimable: boolean;
  /** Новичок: аккаунт создан в этой сессии. */
  newPlayer: boolean;
}

export function progressFlags(s: AppState): ProgressFlags {
  const p = s.player;
  return {
    autoSkills: (p?.run.wave ?? 1) >= balanceV1.waves.autoSkillsFromWave,
    unlockedHeroes: p ? p.heroes.filter((h) => h.unlocked).map((h) => h.heroId) : [],
    offlineIncome: (p?.offline.coins ?? 0) > 0,
    streakClaimable: p ? !p.streak.claimedToday : false,
    newPlayer: s.auth.created,
  };
}
